// Deterministic table -> one-line comments converter.
//
// Why this exists: the AI is reliable at LISTING deductions but unreliable
// at applying them (it lists "(-0.1)" then writes 2/2 in the Marks column).
// The new BLOCK A format is 4 columns:
//   | Question | Marks (max) | Deduction (text with "(-X)" per error) | Deduction Marks (sum) |
// Sub-question rows are the ground truth — we recompute everything else:
//   - per-row deduction = SUM of "(-X)" inside the Deduction text
//   - per-row obtained = max − per-row deduction (clamped to 0)
//   - per-question and grand totals = sums across sub-question rows
//   - the AI's own "Deduction Marks" cell and bold total rows are checked
//     against our compute, never trusted blindly
// Legacy 3-column tables ("Question | Marks (X/Y) | Deduction Summary")
// are still parsed for backward compatibility with already-stored rows.

const SEPARATOR_RE = /^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/;

// The AI sometimes emits TWO BLOCK A tables for the same student (e.g. one
// "draft" without (extra) tagging and a second "final" version with it).
// Without de-duplication every sub-row is parsed twice and the Grand Total
// doubles, and the rendered preview shows both tables stacked.
//
// keepLastTableBlock(md) returns the markdown with all but the LAST table
// block removed (replaced with empty lines so line numbering of any
// surrounding text is preserved). A "table block" = the run of consecutive
// pipe-lines that contains exactly one separator line — i.e. one header +
// one separator + 1..N body rows. Non-pipe lines act as block boundaries.
export function keepLastTableBlock(md) {
  const lines = String(md || '').split('\n');
  // Walk lines, group adjacent pipe-lines into blocks, track which blocks
  // are real tables (contain a separator).
  const blocks = []; // [{ start, end, hasSep }]
  let cur = null;
  for (let i = 0; i < lines.length; i++) {
    const isPipe = lines[i].trim().startsWith('|');
    if (isPipe) {
      if (!cur) cur = { start: i, end: i, hasSep: false };
      else cur.end = i;
      if (SEPARATOR_RE.test(lines[i].trim())) cur.hasSep = true;
    } else if (cur) {
      blocks.push(cur);
      cur = null;
    }
  }
  if (cur) blocks.push(cur);
  const tables = blocks.filter(b => b.hasSep);
  if (tables.length <= 1) return md;
  // Wipe everything except the last table block.
  const lastStart = tables[tables.length - 1].start;
  const lastEnd = tables[tables.length - 1].end;
  const out = lines.slice();
  for (const b of tables) {
    if (b.start === lastStart && b.end === lastEnd) continue;
    for (let i = b.start; i <= b.end; i++) out[i] = '';
  }
  return out.join('\n');
}
const MARKS_RE = /(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/;
const SINGLE_NUM_RE = /(\d+(?:\.\d+)?)/;
const Q_TOTAL_RE = /^\*?\*?\s*(Q[^\s*]+)\s+Total\s*\*?\*?$/i;
const GRAND_RE = /\bgrand\s*total\b/i;
// Optional "(extra)" / "[extra]" tag on the Question id when the student
// over-attempted a "choose N of M" group. Rows tagged extra stay in the
// rendered table but DO NOT contribute to question totals or grand total.
const EXTRA_TAG_RE = /[\(\[]\s*extra\s*[\)\]]/i;
// Part after the question number: i, ii, a, 2, or an Arabic/Urdu label
// such as الف or ب. Latin-only matching collapsed Q.3.الف and Q.3.ب into
// one id, so one of them was left out of the grand total.
const QUESTION_PART = '[A-Za-z0-9\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF]+';

// Deduction penalty tokens. The current checking prompt emits them as
// "[[-0.1]]" (double square brackets); legacy stored rows use the old
// "(-0.1)" parenthesised form. These are parsed with SEPARATE regexes and
// never mixed — see the table-level format switch below.
//
// Why they can't share one regex: a Deduction cell routinely contains
// parenthesised MATH that is NOT a penalty — "f(-2)", "f(-1)=0", "(-x-a+1)".
// The whole reason the format moved to "[[…]]" was to make the penalty
// unambiguous. So when a table uses the "[[…]]" form we must count ONLY
// "[[…]]" and treat every "(…)" as prose; matching "(-2)" here would double
// count "f(-2)" as a 2-mark deduction. Tolerates spaces + decimal comma:
// "[[- 0.1]]", "(-0,5)".
const BRACKET_DEDUCTION_RE = /\[\[\s*[-−–]\s*(\d+(?:[.,]\d+)?)\s*\]\]/g;
const PAREN_DEDUCTION_RE = /\(\s*[-−–]\s*(\d+(?:[.,]\d+)?)\s*\)/g;
// Non-global probe: does this text use the "[[…]]" penalty form anywhere?
const HAS_BRACKET_DEDUCTION_RE = /\[\[\s*[-−–]\s*\d/;

// "Not attempted", "left blank", "no attempt", "no answer", or the literal
// English "Not Attempted[]" / "Not attempted()" markers the prompt emits.
// Urdu equivalents: "حل نہیں کیا", "خالی" (blank), "کوشش نہیں".
// If any of these phrases appears in the Deduction text, the row is a
// zero-out: obtained = 0 regardless of the listed "(-X)" deductions.
const NOT_ATTEMPTED_RE = /\b(?:not\s+attempted|left\s+blank|no\s+attempt(?:ed)?|no\s+answer|blank\s+answer)\b|حل\s*نہیں\s*کیا|کوشش\s*نہیں|خالی\s*جواب/i;

function isNotAttempted(deductionText) {
  return NOT_ATTEMPTED_RE.test(String(deductionText || ''));
}

// "Not attempted:" WITH a colon is the prompt's SUB-POINT form
// ("Not attempted: <sub-point>[[-X]]") — a single missed sub-point inside an
// otherwise-attempted question, NOT a blank whole question. It must never
// trigger the whole-row zero-out, or a mostly-correct answer where the AI
// forgot the [[-X]] on one sub-point (or mislabelled an attempted answer)
// gets nuked to 0. The whole-question blank form has no colon
// ("Not attempted(brief preview)[[-MAX]]").
const SUBPOINT_NOT_ATTEMPTED_RE = /not\s*attempted\s*:/i;

// True only for a WHOLE-question blank/unattempted marker — the case the
// zero-out safety net is meant for. Excludes the "Not attempted:" sub-point
// note above.
function isWholeQuestionUnattempted(deductionText) {
  const text = String(deductionText || '');
  if (!isNotAttempted(text)) return false;
  if (SUBPOINT_NOT_ATTEMPTED_RE.test(text)) return false;
  return true;
}

const EMOJI_NUMS = ['1️⃣','2️⃣','3️⃣','4️⃣','5️⃣','6️⃣','7️⃣','8️⃣','9️⃣','🔟'];

const stripBold = (s) => String(s || '').replace(/\*\*/g, '').trim();
const fmtMark = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));
const round2 = (n) => Math.round(n * 100) / 100;
const clamp0 = (n) => (n < 0 ? 0 : n);

// Sum every penalty inside a Deduction cell. `useBrackets` picks the format:
// true → count ONLY "[[-X]]" tokens (parentheses are treated as prose/math);
// false → legacy mode, count "(-X)" tokens. The caller decides per TABLE so a
// single cell that happens to have no penalty can't flip modes mid-table.
function sumDeductionPenalties(deductionText, useBrackets) {
  const text = String(deductionText || '');
  if (!text) return 0;
  const re = useBrackets ? BRACKET_DEDUCTION_RE : PAREN_DEDUCTION_RE;
  re.lastIndex = 0;
  let total = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const v = parseFloat(m[1].replace(',', '.'));
    if (!isNaN(v)) total += v;
  }
  return round2(total);
}

// Split a markdown table line into cells, dropping the wrapping pipes.
// Does NOT coalesce overflow — use splitMarkdownRowCells once the header
// column count is known.
export function splitRawMarkdownCells(line) {
  let parts = String(line || '').trim().split('|').map(s => s.trim());
  if (parts.length && parts[0] === '') parts = parts.slice(1);
  if (parts.length && parts[parts.length - 1] === '') parts = parts.slice(0, -1);
  return parts;
}

const nearMark = (a, b) => Math.abs(a - b) < 0.051;

// When the AI writes a matrix/determinant with `|` inside the Deduction cell
// (`| 0 | 2 |` / `| 1 | 1 |`), naive split('|') creates extra columns.
// Deduction is the only column that may contain pipes: keep Question + Marks
// at the front, keep the trailing numeric cols when they look like real
// Deduction-Marks / Obtained values, and join everything in between.
export function coalesceOverflowCells(cells, expectedCols) {
  const cols = Number(expectedCols) || 0;
  if (!cols || cells.length <= cols) return cells.slice();
  if (cols < 3) return cells.slice();

  const nTrail = cols - 3;
  const head = cells.slice(0, 2);
  const padTrail = () => [
    ...head,
    cells.slice(2).join(' | '),
    ...Array(Math.max(0, nTrail)).fill('')
  ];

  if (nTrail <= 0) return [...head, cells.slice(2).join(' | ')];
  if (cells.length < 2 + 1 + nTrail) return padTrail();

  const trail = cells.slice(-nTrail);
  const mid = cells.slice(2, cells.length - nTrail);
  const deduction = mid.join(' | ');
  const useBrackets = HAS_BRACKET_DEDUCTION_RE.test(deduction);
  const penalties = sumDeductionPenalties(deduction, useBrackets);
  const maxMatch = stripBold(head[1] || '').match(SINGLE_NUM_RE);
  const max = maxMatch ? parseFloat(maxMatch[1]) : NaN;
  const trailNums = trail.map((c) => {
    const m = stripBold(c).match(SINGLE_NUM_RE);
    return m ? parseFloat(m[1].replace(',', '.')) : NaN;
  });
  const allTrailNumeric = trailNums.every((n) => !Number.isNaN(n));
  const hasPenaltyToken = useBrackets || /\(\s*[-−–]\s*\d/.test(deduction);

  let acceptTrail = false;
  if (allTrailNumeric) {
    const claimedDed = trailNums[0];
    const claimedObt = nTrail >= 2 ? trailNums[1] : NaN;
    const computedObt = Number.isNaN(max) ? NaN : clamp0(round2(max - penalties));
    const marksMatch =
      nearMark(claimedDed, penalties) &&
      (nTrail < 2 || Number.isNaN(claimedObt) || Number.isNaN(computedObt) || nearMark(claimedObt, computedObt));
    if (hasPenaltyToken || marksMatch) acceptTrail = true;
  }

  if (acceptTrail) return [...head, deduction, ...trail];
  return padTrail();
}

export function splitMarkdownRowCells(line, expectedCols) {
  return coalesceOverflowCells(splitRawMarkdownCells(line), expectedCols);
}

export function joinMarkdownRow(cells) {
  return '| ' + cells.map((c) => String(c ?? '').trim()).join(' | ') + ' |';
}

// Patterns the AI uses when it self-corrects inside the Deduction cell
// (e.g. "Corrected: 1.9/2"). Kept for legacy 3-column tables where the
// Marks cell can't be trusted; in 4-column tables we compute from "(-X)" instead.
const SELF_CORRECTION_PATTERNS = [
  /\bCorrected\s*:\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/gi,
  /\bAdjusted\s+to\s*:?\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/gi,
  /\bRevised\s*:\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/gi,
  /\bFinal\s*:\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/gi,
  /\bTotal\s*:[^=]*?=\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/gi
];

function extractSelfCorrection(deductionText) {
  const text = String(deductionText || '');
  if (!text) return null;
  let best = null;
  let bestIdx = -1;
  for (const rx of SELF_CORRECTION_PATTERNS) {
    rx.lastIndex = 0;
    let m;
    while ((m = rx.exec(text)) !== null) {
      if (m.index > bestIdx) {
        bestIdx = m.index;
        best = { obtained: parseFloat(m[1]), max: parseFloat(m[2]) };
      }
    }
  }
  return best;
}

// Header-based format detection by column count.
//   3 cols → legacy "Question | Marks (X/Y) | Deduction Summary"
//   4 cols → "Question | Marks (max) | Deduction | Deduction Marks"
//   5+ cols → "Question | Marks | Deduction | Deduction Marks | Obtained"
function detectFormat(header) {
  if (!header || header.length < 3) return 'legacy3';
  if (header.length >= 5) return 'new5';
  if (header.length === 4) return 'new4';
  return 'legacy3';
}

// "Q.4.i" and "Q4.i" are the same part of question 4. The parent is the
// question number, not the text before the first dot — "Q.1.i".split('.')[0]
// is "Q", which collapsed every dotted label into one fake question.
function questionParentLabel(cleanId) {
  const norm = normalizeQuestionId(cleanId);
  const m = norm && norm.match(/^q(\d+)/i);
  if (m) return `Q${m[1]}`;
  return cleanId;
}

function dedupeSubQuestions(rows) {
  const indexByNorm = new Map();
  const out = [];
  for (const sq of rows) {
    const key = normalizeQuestionId(sq.id);
    if (!key) {
      out.push(sq);
      continue;
    }
    if (indexByNorm.has(key)) {
      out[indexByNorm.get(key)] = sq;
      continue;
    }
    indexByNorm.set(key, out.length);
    out.push(sq);
  }
  return out;
}

export function parseDeductionTable(md) {
  // Defensive de-dupe: if the AI emitted two table blocks we only want the
  // last one (the "final" one with corrected tagging). Without this every
  // sub-row is parsed twice and the Grand Total doubles.
  const text = keepLastTableBlock(String(md || '').trim()).trim();
  if (!text || !text.includes('|')) return null;
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.startsWith('|'));
  if (lines.length < 2) return null;

  let header = null;
  const rows = [];
  for (const line of lines) {
    if (SEPARATOR_RE.test(line)) continue;
    if (!header) { header = splitRawMarkdownCells(line); continue; }
    rows.push(splitMarkdownRowCells(line, header.length));
  }
  if (!rows.length) return null;

  const format = detectFormat(header);
  // Table-level penalty format. If ANY cell uses the "[[-X]]" form, the whole
  // table is treated as new-format and we count ONLY "[[-X]]" — so math like
  // "f(-2)" in a Deduction cell is never mistaken for a 2-mark penalty. Old
  // stored tables (no "[[…]]" anywhere) fall back to the legacy "(-X)" form.
  const tableUsesBrackets = HAS_BRACKET_DEDUCTION_RE.test(text);
  const subQuestions = [];
  const aiQuestionTotals = [];
  let aiGrandTotal = null;
  // Rows written after the Grand Total are unmatched labels. They stay
  // visible under the table and add nothing to the score.
  let pastGrand = false;

  for (const cells of rows) {
    if (pastGrand) continue;
    if (cells.length < 2) continue;
    const idRaw = cells[0] || '';
    const idStripped = stripBold(idRaw);
    const isGrand = GRAND_RE.test(idStripped);
    const qTotalMatch = idStripped.match(Q_TOTAL_RE);

    if (format === 'new4' || format === 'new5') {
      // 4-col: Question | Marks (max) | Deduction | Deduction Marks
      // 5-col: ... | Obtained
      // Trust sub-question rows: deduction = sum of "(-X)" in the Deduction
      // cell; obtained = max − deduction. The AI's "Deduction Marks" and
      // "Obtained" cells are captured but never used as truth (correctTable
      // rewrites them to match).
      const maxRaw = stripBold(cells[1] || '');
      const deduction = stripBold(cells[2] || '');
      const maxMatch = maxRaw.match(SINGLE_NUM_RE);
      if (!maxMatch) continue;
      const max = parseFloat(maxMatch[1]);

      if (isGrand) {
        aiGrandTotal = { max };
        pastGrand = true;
        continue;
      }
      if (qTotalMatch) {
        aiQuestionTotals.push({ id: qTotalMatch[1], max });
        continue;
      }
      // Strip the "(extra)" tag (if any) before computing the parent so
      // grouping is unaffected — the flag is what excludes the row from
      // totals downstream. rawId preserves the exact AI label so correctTable
      // can still look the row up; cleanId/id is the canonical label without
      // the tag, used for grouping and for re-tagging after auto-correction.
      const isExtra = EXTRA_TAG_RE.test(idStripped);
      const rawId = idStripped;
      const cleanId = idStripped.replace(EXTRA_TAG_RE, '').trim();
      const id = cleanId;
      const parent = questionParentLabel(cleanId);
      // Deduction marks = SUM of the explicit "[[-X]]" penalties in the cell.
      // The "Not attempted" zero-out is only a SAFETY NET for when the AI tags
      // a row as unattempted but forgets to write any "[[-MAX]]" penalty. It must
      // NOT fire when the cell already carries "[[-X]]" penalties: with one row
      // per QUESTION, a partially-attempted question lists "Not attempted:
      // <sub-point>[[-X]]" for the sub-points it missed while still earning marks
      // on the parts it attempted — so we trust the penalty sum whenever any
      // explicit penalty exists, and only force deduction = max when the row
      // reads as unattempted AND has no penalty at all.
      const penaltySum = sumDeductionPenalties(deduction, tableUsesBrackets);
      const deductionMarks = penaltySum > 0
        ? penaltySum
        : (isWholeQuestionUnattempted(deduction) ? max : 0);
      const obtained = clamp0(round2(max - deductionMarks));
      subQuestions.push({ id, rawId, parent, max, deduction, deductionMarks, obtained, isExtra });
      continue;
    }

    // Legacy 3-column: Question | Marks (X/Y) | Deduction Summary
    const marksRaw = stripBold(cells[1] || '');
    const deduction = stripBold(cells[2] || '');
    const m = marksRaw.match(MARKS_RE);
    if (!m) continue;
    let obtained = parseFloat(m[1]);
    let max = parseFloat(m[2]);

    if (isGrand) {
      aiGrandTotal = { obtained, max };
      pastGrand = true;
      continue;
    }
    if (qTotalMatch) {
      aiQuestionTotals.push({ id: qTotalMatch[1], obtained, max });
      continue;
    }
    const sc = extractSelfCorrection(deduction);
    if (sc && !isNaN(sc.obtained) && !isNaN(sc.max) && sc.max > 0) {
      obtained = sc.obtained;
      if (sc.max !== max && sc.obtained <= sc.max) max = sc.max;
    }
    // Zero-out for "Not attempted" / "Blank" / "No answer".
    if (isNotAttempted(deduction)) obtained = 0;
    const isExtra = EXTRA_TAG_RE.test(idStripped);
    const rawId = idStripped;
    const cleanId = idStripped.replace(EXTRA_TAG_RE, '').trim();
    const id = cleanId;
    const parent = questionParentLabel(cleanId);
    const deductionMarks = round2(Math.max(0, max - obtained));
    subQuestions.push({ id, rawId, parent, max, deduction, deductionMarks, obtained, isExtra });
  }

  return { format, subQuestions: dedupeSubQuestions(subQuestions), aiQuestionTotals, aiGrandTotal };
}

export function computeTrueTotals(md, paperTotal) {
  const parsed = parseDeductionTable(md);
  if (!parsed || !parsed.subQuestions.length) return null;
  const { format, subQuestions, aiQuestionTotals, aiGrandTotal } = parsed;

  const groupOrder = [];
  const groupsMap = new Map();
  for (const sq of subQuestions) {
    if (!groupsMap.has(sq.parent)) {
      groupsMap.set(sq.parent, []);
      groupOrder.push(sq.parent);
    }
    groupsMap.get(sq.parent).push(sq);
  }
  groupOrder.sort((a, b) => {
    const na = parseInt(String(a).replace(/\D/g, ''), 10) || 0;
    const nb = parseInt(String(b).replace(/\D/g, ''), 10) || 0;
    return na - nb;
  });
  // A bare "Q4" row is a rollup. Once the parts (Q4.i, Q4.iv) are present,
  // counting it again would put 5/5 on top of those parts.
  for (const parent of groupOrder) {
    const list = groupsMap.get(parent);
    const parts = list.filter(sq => {
      const norm = normalizeQuestionId(sq.id);
      return norm && norm.includes('.');
    });
    if (parts.length) groupsMap.set(parent, parts);
  }

  // AUTO-CORRECTION FOR "(extra)" TAGGING:
  // The AI is unreliable at picking WHICH row to mark extra — it tends to
  // tag whichever question the student attempted last, even when that row
  // scored higher than other rows in the same choice group. The rule is
  // "drop the LOWEST-scoring (M − N) rows". So: for any group that has at
  // least one extra-tagged row, we recount K = number of extras and then
  // reassign isExtra to the K lowest-Obtained rows in that group. Ties on
  // Obtained break to the row that appears LATER in the table (matches the
  // prompt's tie-breaker). Rows that were tagged but shouldn't be have
  // their flag cleared; rows that should be tagged but weren't get it set.
  // displayId is what correctTable writes into the Question column.
  for (const parent of groupOrder) {
    const list = groupsMap.get(parent);
    const flaggedCount = list.reduce((n, x) => n + (x.isExtra ? 1 : 0), 0);
    if (flaggedCount === 0) {
      list.forEach(sq => { sq.displayId = sq.id; });
      continue;
    }
    const ranked = list
      .map((sq, idx) => ({ sq, idx }))
      .sort((a, b) => (a.sq.obtained - b.sq.obtained) || (b.idx - a.idx));
    const trueExtras = new Set(ranked.slice(0, flaggedCount).map(r => r.idx));
    list.forEach((sq, idx) => {
      sq.isExtra = trueExtras.has(idx);
      sq.displayId = sq.isExtra ? `${sq.id} (extra)` : sq.id;
    });
  }

  // Rows tagged "(extra)" (post auto-correction) are over-attempts in a
  // "choose N of M" group. They stay in the rendered table for audit but
  // DO NOT add into per-question totals or the grand total.
  const questionTotals = groupOrder.map(parent => {
    const list = groupsMap.get(parent).filter(x => !x.isExtra);
    return {
      id: parent,
      max: round2(list.reduce((s, x) => s + x.max, 0)),
      deductionMarks: round2(list.reduce((s, x) => s + x.deductionMarks, 0)),
      obtained: round2(list.reduce((s, x) => s + x.obtained, 0))
    };
  });

  const summedMax = round2(questionTotals.reduce((s, t) => s + t.max, 0));
  const paper = Number(paperTotal);
  const grandTotal = {
    max: (Number.isFinite(paper) && paper > 0) ? round2(paper) : summedMax,
    deductionMarks: round2(questionTotals.reduce((s, t) => s + t.deductionMarks, 0)),
    obtained: round2(questionTotals.reduce((s, t) => s + t.obtained, 0))
  };

  return {
    format,
    subQuestions,
    groupOrder,
    groupsMap,
    questionTotals,
    grandTotal,
    aiQuestionTotals,
    aiGrandTotal
  };
}

export function gradeFromTable(md, opts = {}) {
  const sanitized = sanitizeBlockA(md, opts.allowedIds, opts.rubricMarks, opts.paperTotal);
  const t = computeTrueTotals(sanitized.md, opts.paperTotal);
  return t ? { obtained: t.grandTotal.obtained, max: t.grandTotal.max } : null;
}

/** Normalize "Q.3.iii", "Q3.iii", "Q.3.الف", "Q 3.iii (extra)" → "q3.iii" / "q3.الف" */
export function normalizeQuestionId(raw) {
  let s = String(raw || '').replace(/\*\*/g, '').trim();
  s = s.replace(EXTRA_TAG_RE, '').trim();
  const m = s.match(new RegExp(`^Q\\.?\\s*(\\d+)\\s*[.)]?\\s*(${QUESTION_PART})?`, 'i'));
  if (!m) return null;
  const num = m[1];
  const sub = (m[2] || '').toLowerCase();
  return sub ? `q${num}.${sub}` : `q${num}`;
}

/**
 * Written labels that are not rubric questions. Rows already under the
 * Grand Total count, and so does a body row whose label cannot be placed
 * on a rubric id. Same rule as the Evaluation folder card.
 */
export function listUnmatchedQuestionLabels(md, allowedIds) {
  if (!md || !allowedIds || !(allowedIds instanceof Set) || allowedIds.size === 0) return [];
  const labels = [];
  const seen = new Set();
  const push = (label) => {
    const text = String(label || '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) return;
    seen.add(key);
    labels.push(text);
  };
  let pastGrand = false;
  for (const rawLine of String(md).split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('|') || SEPARATOR_RE.test(line)) continue;
    const cells = line.split('|');
    if (cells.length < 4) continue;
    const label = stripBold(cells[1] || '');
    if (!label || /^question$/i.test(label)) continue;
    if (GRAND_RE.test(label)) { pastGrand = true; continue; }
    if (Q_TOTAL_RE.test(label)) continue;
    if (pastGrand) {
      push(stripBold(cells[1]).replace(EXTRA_TAG_RE, '').trim());
      continue;
    }
    const normId = normalizeQuestionId(label);
    if (normId && isAllowedQuestionId(normId, allowedIds)) continue;
    if (rubricTargetFromNote(stripBold(cells[3] || ''), allowedIds)) continue;
    push(stripBold(cells[1]).replace(EXTRA_TAG_RE, '').trim() || label);
  }
  return labels;
}

/** Exact id, or a whole-question row (q1) covering rubric sub-points (q1.i). */
function isAllowedQuestionId(normId, allowedIds) {
  if (!normId || !allowedIds || !(allowedIds instanceof Set) || allowedIds.size === 0) {
    return true;
  }
  if (allowedIds.has(normId)) return true;
  if (!normId.includes('.')) {
    for (const id of allowedIds) {
      if (id.startsWith(normId + '.')) return true;
    }
  }
  return false;
}

/**
 * Parse allowed BLOCK A question IDs from rubric (or grading-prompt) text.
 * Only lines that look like question headers (start with Q…, ideally with marks)
 * count — never invent IDs from prose or Rubrics Breakdown tables.
 */
export function extractRubricQuestionIds(rubricText) {
  const ids = new Set();
  if (!rubricText) return ids;
  for (const rawLine of String(rubricText).split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    if (/rubrics?\s*breakdown|sub-?step\s*table|model\s*answer\s*:/i.test(line)) continue;
    // Prefer header-looking lines: "Q.N … (M marks)" or "Q.N.sub - …"
    const looksLikeHeader =
      /^Q\.?\s*\d+/i.test(line) &&
      (/\(\s*\d+(?:\.\d+)?\s*marks?\s*\)/i.test(line) ||
        /^Q\.?\s*\d+\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+/i.test(line) ||
        /^Q\.?\s*\d+\s*[-–—:]/i.test(line));
    if (!looksLikeHeader) continue;
    const labelMatch = line.match(/^Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?/i);
    const id = normalizeQuestionId(labelMatch?.[0] || '');
    if (id) ids.add(id);
  }
  return ids;
}

// Same window the Sort Papers tag uses: the first "total marks" in the rubric.
export function paperTotalFromRubric(text) {
  const lower = String(text || '').toLowerCase();
  const at = lower.indexOf('total marks');
  if (at < 0) return null;
  const start = Math.max(at - 15, 0);
  const snip = lower.slice(start, start + 80).replace(/\*/g, '');
  const patterns = [
    /total\s*marks\s*:\s*(\d+(?:\.\d+)?)/,
    /total\s*marks\s*:\s*\/\s*(\d+(?:\.\d+)?)/,
    /total\s*marks\s*:\s*_+\s*\/\s*(\d+(?:\.\d+)?)/
  ];
  for (const re of patterns) {
    const match = snip.match(re);
    if (!match) continue;
    const n = parseFloat(match[1]);
    if (n > 0) return n;
  }
  return null;
}

/** normId → max marks from rubric headers like "Q5 - Define condensation (3 marks)". */
export function extractRubricQuestionMarks(rubricText) {
  const marks = new Map();
  if (!rubricText) return marks;
  for (const rawLine of String(rubricText).split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    if (/rubrics?\s*breakdown|sub-?step\s*table|model\s*answer\s*:/i.test(line)) continue;
    const looksLikeHeader =
      /^Q\.?\s*\d+/i.test(line) &&
      (/\(\s*\d+(?:\.\d+)?\s*marks?\s*\)/i.test(line) ||
        /^Q\.?\s*\d+\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+/i.test(line) ||
        /^Q\.?\s*\d+\s*[-–—:]/i.test(line));
    if (!looksLikeHeader) continue;
    const labelMatch = line.match(/^Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?/i);
    const id = normalizeQuestionId(labelMatch?.[0] || '');
    const marksMatch = line.match(/\(\s*(\d+(?:\.\d+)?)\s*marks?\s*\)/i);
    if (id && marksMatch && !marks.has(id)) marks.set(id, parseFloat(marksMatch[1]));
  }
  return marks;
}

/** "q1" / "Q.1.i" → "Q1" / "Q1.i" for the question picker. */
export function formatQuestionLabel(id) {
  const norm = normalizeQuestionId(id) || String(id || '').trim();
  const m = String(norm).match(/^q(\d+)(?:\.(.+))?$/i);
  if (!m) return String(id || '');
  return m[2] ? `Q${m[1]}.${m[2]}` : `Q${m[1]}`;
}

/** Rubric question headers in paper order: [{ id: "q1", label: "Q1" }, ...]. */
export function listRubricQuestions(rubricText) {
  const out = [];
  const seen = new Set();
  if (!rubricText) return out;
  for (const rawLine of String(rubricText).split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    if (/rubrics?\s*breakdown|sub-?step\s*table|model\s*answer\s*:/i.test(line)) continue;
    const looksLikeHeader =
      /^Q\.?\s*\d+/i.test(line) &&
      (/\(\s*\d+(?:\.\d+)?\s*marks?\s*\)/i.test(line) ||
        /^Q\.?\s*\d+\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+/i.test(line) ||
        /^Q\.?\s*\d+\s*[-–—:]/i.test(line));
    if (!looksLikeHeader) continue;
    const labelMatch = line.match(/^Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?/i);
    const id = normalizeQuestionId(labelMatch?.[0] || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label: formatQuestionLabel(id) });
  }
  return out;
}

// "Question number incorrect (should be Q3.iii)[[-0.5]]" → target "q3.iii"
const SHOULD_BE_RE = /should\s+be\s+(Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?)/i;
// A wrong written question label. Old rows say "Question number incorrect";
// new rows say "QN.INC (labeled as Q...)[[-0.1]]". Either one is the −0.1.
const HAS_QN_PENALTY_RE = /(?:question\s+number\s+incorrect|QN\.INC)\b/i;
// One penalty span (no trailing whitespace eater) so we can drop order-only /
// matching-label penalties without touching a genuine mislabel on the same row.
const Q_NUM_INCORRECT_ITEM_RE =
  /(?:^|[.]\s*)?(?:Question\s+number\s+incorrect|QN\.INC)(?:\s*\([^)]*\))?(?:\s*\[\[[^\]]*\]\])?/gi;
const LABELED_AS_RE = /labeled\s+as\s+(Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?)/i;
const ORDER_ONLY_REASON_RE =
  /\b(?:before|after|out\s+of\s+order|order\s+(?:is\s+)?chang(?:ed|e)|not\s+in\s+(?:numerical\s+)?order|sequence)\b/i;

/**
 * Keep a genuine mislabel penalty; drop "labeled as Q4 before Q3" on the Q4
 * row (student number matches — only the page order changed).
 */
export function shouldKeepQuestionNumberIncorrect(chunk, rowNormId) {
  const labeled = String(chunk || '').match(LABELED_AS_RE);
  if (labeled) {
    const labeledNorm = normalizeQuestionId(labeled[1]);
    if (labeledNorm && rowNormId && labeledNorm === rowNormId) return false;
    return true;
  }
  if (ORDER_ONLY_REASON_RE.test(chunk)) return false;
  return true;
}

function tidyDeductionAfterStrip(text) {
  return String(text || '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[.\s,;:]+/, '')
    .replace(/[.\s]+$/g, '')
    .trim();
}

// Add QN.INC only when the written label is a different question. Cap it at
// the marks still left after the content deductions. Omit it when nothing is left.
function ensureQuestionNumberPenalty(deduction, writtenLabel, maxMarks) {
  const text = tidyDeductionAfterStrip(deduction);
  if (HAS_QN_PENALTY_RE.test(text)) return text;
  const label = String(writtenLabel || 'Q?').replace(EXTRA_TAG_RE, '').trim() || 'Q?';
  const amount = cappedQuestionNumberDeduction(text, maxMarks);
  if (amount <= 0) return text;
  const penalty = `QN.INC (labeled as ${label})[[-${fmtMark(amount)}]]`;
  return text ? `${text}. ${penalty}` : penalty;
}

function cappedQuestionNumberDeduction(contentText, maxMarks) {
  const max = parseFloat(maxMarks);
  if (!Number.isFinite(max)) return 0.1;
  const remaining = Math.max(0, round2(max - sumDeductionPenalties(contentText, true)));
  return Math.min(0.1, remaining);
}

/**
 * Remove the −0.1 only when the written label already matches the rubric row
 * (Q4 answered before Q3 is not a mislabel). A real wrong label, written as
 * QN.INC or the older "Question number incorrect", stays.
 */
export function stripMatchingLabelQuestionNumberPenalties(md) {
  if (!md) return { md, changed: false };
  const lines = String(md).split('\n');
  let expectedCols = 0;
  let changed = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim().startsWith('|')) continue;
    if (SEPARATOR_RE.test(line.trim())) continue;
    const raw = splitRawMarkdownCells(line);
    if (raw.length < 3) continue;
    const idStripped = stripBold(raw[0] || '');
    if (/^question$/i.test(idStripped)) {
      expectedCols = raw.length;
      continue;
    }
    if (GRAND_RE.test(idStripped) || Q_TOTAL_RE.test(idStripped)) continue;
    const cells = splitMarkdownRowCells(line, expectedCols || raw.length);
    if (cells.length < 3) continue;
    const normId = normalizeQuestionId(idStripped);
    if (!normId) continue;

    const deductionIdx = 2;
    const wasBold = /\*\*/.test(cells[deductionIdx] || '');
    const deduction = stripBold(cells[deductionIdx] || '');
    if (!HAS_QN_PENALTY_RE.test(deduction)) continue;

    Q_NUM_INCORRECT_ITEM_RE.lastIndex = 0;
    const next = deduction.replace(Q_NUM_INCORRECT_ITEM_RE, (chunk) => (
      shouldKeepQuestionNumberIncorrect(chunk, normId) ? chunk : ''
    ));
    if (next === deduction) continue;
    const cleaned = tidyDeductionAfterStrip(next);
    cells[deductionIdx] = wasBold ? `**${cleaned}**` : cleaned;
    lines[i] = joinMarkdownRow(cells);
    changed = true;
  }

  return { md: changed ? lines.join('\n') : md, changed };
}

// A whole-question row (q4) already stands for its parts, and part rows
// (q4.a) already stand for the parent. Either one means the question is
// in the table.
function questionCovered(id, present) {
  if (present.has(id)) return true;
  const dot = id.indexOf('.');
  if (dot > 0 && present.has(id.slice(0, dot))) return true;
  if (dot < 0) {
    const prefix = `${id}.`;
    for (const other of present) {
      if (other.startsWith(prefix)) return true;
    }
  }
  return false;
}

function insertedQuestionLabel(normId, sampleLabel) {
  const m = String(normId || '').match(/^q(\d+)(?:\.(.+))?$/i);
  if (!m) return formatQuestionLabel(normId);
  const dotted = /^Q\s*\./i.test(sampleLabel || '');
  const head = dotted ? `Q.${m[1]}` : `Q${m[1]}`;
  return m[2] ? `${head}.${m[2]}` : head;
}

function missingQuestionRow(label, max, cols) {
  const mark = fmtMark(round2(max));
  const ded = `Not Attempted[[-${mark}]]`;
  if (cols >= 5) return `| ${label} | ${mark} | ${ded} | ${mark} | 0 |`;
  if (cols === 4) return `| ${label} | ${mark} | ${ded} | ${mark} |`;
  return `| ${label} | 0/${mark} | ${ded} |`;
}

function maxInCells(cells) {
  if (!cells || cells.length < 2) return 0;
  const text = stripBold(cells[1]);
  if (cells.length <= 3) {
    const frac = text.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (frac) return parseFloat(frac[2]);
  }
  const n = text.match(/(\d+(?:\.\d+)?)/);
  return n ? parseFloat(n[1]) : 0;
}

/**
 * Every rubric question gets a row. A question the table skipped is inserted
 * as Not Attempted for its own marks, so the paper maximum stays the rubric
 * total. On "attempt any N", a spare that would push the maximum past the
 * rubric total is left out. A table that already has the question (or its
 * parent / its parts) is unchanged.
 */
function insertMissingRubricQuestions(md, rubricMarks, paperTotal) {
  if (!md || !rubricMarks || typeof rubricMarks.forEach !== 'function' || rubricMarks.size === 0) {
    return { md: String(md || ''), changed: false };
  }
  const lines = String(md).split('\n');
  let headerIdx = -1;
  let grandIdx = -1;
  let cols = 5;
  const present = new Set();
  const lineOf = new Map();
  let sampleLabel = '';
  let countedMax = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim().startsWith('|') || SEPARATOR_RE.test(line.trim())) continue;
    const cells = splitRawMarkdownCells(line);
    if (cells.length < 2) continue;
    const label = stripBold(cells[0] || '');
    if (/^question$/i.test(label)) {
      headerIdx = i;
      cols = cells.length >= 5 ? 5 : cells.length;
      present.clear();
      lineOf.clear();
      sampleLabel = '';
      countedMax = 0;
      grandIdx = -1;
      continue;
    }
    if (headerIdx < 0) continue;
    if (GRAND_RE.test(label)) { grandIdx = i; continue; }
    if (grandIdx >= 0) continue;
    if (Q_TOTAL_RE.test(label)) continue;
    const id = normalizeQuestionId(label);
    if (!id) continue;
    if (!sampleLabel) sampleLabel = label.replace(EXTRA_TAG_RE, '').trim();
    if (!lineOf.has(id)) lineOf.set(id, i);
    present.add(id);
    if (!EXTRA_TAG_RE.test(label)) countedMax = round2(countedMax + maxInCells(cells));
  }
  if (headerIdx < 0 || present.size === 0) return { md, changed: false };

  let room = (typeof paperTotal === 'number' && paperTotal > 0) ? round2(paperTotal - countedMax) : null;
  const additions = [];
  for (const [id, marks] of rubricMarks) {
    if (questionCovered(id, present)) continue;
    const max = Number(marks);
    if (!(max > 0)) continue;
    if (room != null && max > room + 0.05) continue;
    additions.push({ id, max });
    present.add(id);
    if (room != null) room = round2(room - max);
  }
  if (!additions.length) return { md, changed: false };

  const rubricIds = [];
  for (const id of rubricMarks.keys()) rubricIds.push(id);
  const fallbackAt = grandIdx >= 0 ? grandIdx : lines.length;
  const buckets = new Map();
  for (const item of additions) {
    const idx = rubricIds.indexOf(item.id);
    let at = fallbackAt;
    for (let j = idx + 1; j < rubricIds.length; j++) {
      const later = rubricIds[j];
      if (lineOf.has(later)) { at = lineOf.get(later); break; }
      const parent = later.includes('.') ? later.split('.')[0] : '';
      if (parent && lineOf.has(parent)) { at = lineOf.get(parent); break; }
    }
    if (!buckets.has(at)) buckets.set(at, []);
    buckets.get(at).push(missingQuestionRow(insertedQuestionLabel(item.id, sampleLabel), item.max, cols));
  }
  const ats = [...buckets.keys()].sort((a, b) => b - a);
  for (const at of ats) lines.splice(at, 0, ...buckets.get(at));
  return { md: lines.join('\n'), changed: true };
}

function sanitizeBlockA(md, allowedIds, rubricMarks, paperTotal) {
  let working = String(md || '');
  let changed = false;
  const ids = allowedIds instanceof Set
    ? allowedIds
    : (Array.isArray(allowedIds) ? new Set(allowedIds) : null);
  if (ids && ids.size > 0) {
    const filtered = filterPhantomQuestionRows(working, ids);
    working = filtered.md;
    if (filtered.changed) changed = true;
  }
  const reassigned = reassignMislabeledAnswerRows(working, rubricMarks);
  working = reassigned.md;
  if (reassigned.changed) changed = true;
  const stripped = stripMatchingLabelQuestionNumberPenalties(working);
  working = stripped.md;
  if (stripped.changed) changed = true;
  const filled = insertMissingRubricQuestions(working, rubricMarks, paperTotal);
  working = filled.md;
  if (filled.changed) changed = true;
  return { md: working, changed };
}

// "answered / wrote / written / attempted … as Q5".
// A match sitting inside "not attempted" is ignored.
const ANSWERED_UNDER_RE =
  /(?:answered|wrote|written|attempted)\s+(?:a\s+)?(?:partial\s+)?(?:version\s+)?(?:of\s+)?(?:this\s+)?(?:answer\s+)?(?:it\s+)?as\s+(Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?)/ig;

function matchAnsweredUnder(deduction) {
  const text = String(deduction || '');
  ANSWERED_UNDER_RE.lastIndex = 0;
  let m;
  while ((m = ANSWERED_UNDER_RE.exec(text)) !== null) {
    const before = text.slice(Math.max(0, m.index - 4), m.index);
    if (/not\s$/i.test(before)) continue;
    return m;
  }
  return null;
}

function cellNumber(cell) {
  const m = stripBold(cell).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
}

function writeMarkCell(oldCell, value) {
  const wasBold = /\*\*/.test(oldCell || '');
  const text = fmtMark(round2(value));
  return wasBold ? `**${text}**` : text;
}

function writeDeductionCell(oldCell, text) {
  const wasBold = /\*\*/.test(oldCell || '');
  return wasBold ? `**${text}**` : text;
}

function rubricMaxFor(normId, rubricMarks, fallbackCell) {
  if (rubricMarks && typeof rubricMarks.get === 'function' && rubricMarks.has(normId)) {
    const n = rubricMarks.get(normId);
    if (typeof n === 'number' && !Number.isNaN(n)) return n;
  }
  const m = stripBold(fallbackCell).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
}

function hasGradeableContent(deduction) {
  const t = String(deduction || '').trim();
  if (!t) return false;
  if (/^(full marks?|correct|none|—|-)$/i.test(t)) return false;
  if (isWholeQuestionUnattempted(t)) return false;
  return true;
}

function displayQuestionLabel(cell) {
  return stripBold(cell).replace(EXTRA_TAG_RE, '').trim();
}

/**
 * The model sometimes grades a mislabeled block on the number the student
 * wrote, and zeros the real question with "answered a partial version as Qn".
 * Move that working onto the question the note names, keep a −0.1 mislabel
 * penalty there, and mark the written-number row not attempted at its own max.
 */
export function reassignMislabeledAnswerRows(md, rubricMarks) {
  if (!md) return { md, changed: false };
  const lines = String(md).split('\n');
  let expectedCols = 0;
  const bodyRows = [];
  let grand = null;
  let pastGrand = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim().startsWith('|')) continue;
    if (SEPARATOR_RE.test(line.trim())) continue;
    const raw = splitRawMarkdownCells(line);
    if (raw.length < 3) continue;
    const idStripped = stripBold(raw[0] || '');
    if (/^question$/i.test(idStripped)) {
      expectedCols = raw.length;
      continue;
    }
    const cells = splitMarkdownRowCells(line, expectedCols || raw.length);
    if (cells.length < 3) continue;
    if (GRAND_RE.test(idStripped)) {
      grand = { lineIdx: i, cells };
      pastGrand = true;
      continue;
    }
    if (pastGrand) continue;
    if (Q_TOTAL_RE.test(idStripped)) continue;
    const normId = normalizeQuestionId(idStripped);
    if (!normId) continue;
    bodyRows.push({ lineIdx: i, cells, normId, deductionIdx: 2 });
  }

  const byNorm = new Map();
  for (const row of bodyRows) {
    if (!byNorm.has(row.normId)) byNorm.set(row.normId, []);
    byNorm.get(row.normId).push(row);
  }

  const moves = [];
  const used = new Set();

  const queue = (source, target) => {
    if (!source || !target) return;
    if (source.lineIdx === target.lineIdx) return;
    if (used.has(source.lineIdx) || used.has(target.lineIdx)) return;
    const sourceDed = stripBold(source.cells[2] || '');
    if (!hasGradeableContent(sourceDed)) return;
    used.add(source.lineIdx);
    used.add(target.lineIdx);
    moves.push({ source, target, sourceDed });
  };

  for (const row of bodyRows) {
    const ded = stripBold(row.cells[2] || '');
    if (!isWholeQuestionUnattempted(ded)) continue;
    const named = matchAnsweredUnder(ded);
    if (!named) continue;
    const sourceNorm = normalizeQuestionId(named[1]);
    if (!sourceNorm || sourceNorm === row.normId) continue;
    const sources = byNorm.get(sourceNorm) || [];
    if (sources.length !== 1) continue;
    queue(sources[0], row);
  }

  for (const row of bodyRows) {
    const ded = stripBold(row.cells[2] || '');
    const named = ded.match(SHOULD_BE_RE);
    if (!named) continue;
    const targetNorm = normalizeQuestionId(named[1]);
    if (!targetNorm || targetNorm === row.normId) continue;
    const targets = byNorm.get(targetNorm) || [];
    if (targets.length !== 1) continue;
    const targetDed = stripBold(targets[0].cells[2] || '');
    if (targetDed.trim() && !isWholeQuestionUnattempted(targetDed)) continue;
    queue(row, targets[0]);
  }

  if (moves.length === 0) return { md, changed: false };

  let deltaMax = 0;
  let deltaDed = 0;
  let deltaObt = 0;

  for (const { source, target, sourceDed } of moves) {
    const sourceMax = rubricMaxFor(source.normId, rubricMarks, source.cells[1]);
    const targetMax = rubricMaxFor(target.normId, rubricMarks, target.cells[1]);
    const label = displayQuestionLabel(source.cells[0]) || 'Q?';
    const moved = ensureQuestionNumberPenalty(sourceDed, label, targetMax);
    const movedPenalties = sumDeductionPenalties(moved, true);
    const movedObt = clamp0(round2(targetMax - movedPenalties));
    const blank = `Not attempted[[-${fmtMark(sourceMax)}]]`;
    const blankPenalties = sourceMax;
    const blankObt = 0;

    const oldSourceMax = cellNumber(source.cells[1]);
    const oldSourceDed = cellNumber(source.cells[3]);
    const oldSourceObt = source.cells.length >= 5
      ? cellNumber(source.cells[4])
      : clamp0(round2(oldSourceMax - oldSourceDed));
    const oldTargetMax = cellNumber(target.cells[1]);
    const oldTargetDed = cellNumber(target.cells[3]);
    const oldTargetObt = target.cells.length >= 5
      ? cellNumber(target.cells[4])
      : clamp0(round2(oldTargetMax - oldTargetDed));

    deltaMax += (sourceMax + targetMax) - (oldSourceMax + oldTargetMax);
    deltaDed += (blankPenalties + movedPenalties) - (oldSourceDed + oldTargetDed);
    deltaObt += (blankObt + movedObt) - (oldSourceObt + oldTargetObt);

    source.cells[1] = writeMarkCell(source.cells[1], sourceMax);
    source.cells[2] = writeDeductionCell(source.cells[2], blank);
    if (source.cells.length >= 4) source.cells[3] = writeMarkCell(source.cells[3], blankPenalties);
    if (source.cells.length >= 5) source.cells[4] = writeMarkCell(source.cells[4], blankObt);

    target.cells[1] = writeMarkCell(target.cells[1], targetMax);
    target.cells[2] = writeDeductionCell(target.cells[2], moved);
    if (target.cells.length >= 4) target.cells[3] = writeMarkCell(target.cells[3], movedPenalties);
    if (target.cells.length >= 5) target.cells[4] = writeMarkCell(target.cells[4], movedObt);

    lines[source.lineIdx] = joinMarkdownRow(source.cells);
    lines[target.lineIdx] = joinMarkdownRow(target.cells);
  }

  if (grand && grand.cells.length >= 4) {
    grand.cells[1] = writeMarkCell(grand.cells[1], cellNumber(grand.cells[1]) + deltaMax);
    grand.cells[3] = writeMarkCell(grand.cells[3], cellNumber(grand.cells[3]) + deltaDed);
    if (grand.cells.length >= 5) {
      grand.cells[4] = writeMarkCell(grand.cells[4], clamp0(cellNumber(grand.cells[4]) + deltaObt));
    }
    lines[grand.lineIdx] = joinMarkdownRow(grand.cells);
  }

  return { md: lines.join('\n'), changed: true };
}

// "labeled as Qz.xiii" — z stood in for the question number. Use the part
// (xiii) when exactly one rubric id ends with it.
const LABELED_AS_Z_RE = /labeled\s+as\s+Q\s*z\s*\.?\s*([A-Za-z]+)/i;

function rubricTargetFromNote(deduction, allowedIds) {
  const text = String(deduction || '');
  const shouldBe = text.match(SHOULD_BE_RE);
  if (shouldBe) {
    const id = normalizeQuestionId(shouldBe[1]);
    if (id && allowedIds.has(id)) return id;
  }
  const labeled = text.match(LABELED_AS_RE);
  if (labeled) {
    const id = normalizeQuestionId(labeled[1]);
    if (id && allowedIds.has(id)) return id;
  }
  const loose = text.match(LABELED_AS_Z_RE);
  if (!loose) return null;
  const sub = loose[1].toLowerCase();
  const hits = [];
  for (const id of allowedIds) {
    if (id.endsWith(`.${sub}`)) hits.push(id);
  }
  return hits.length === 1 ? hits[0] : null;
}

function writeRowDeduction(cells, deduction) {
  const max = cellNumber(cells[1]);
  const penalties = sumDeductionPenalties(deduction, true);
  const ded = penalties > 0 ? penalties : (isWholeQuestionUnattempted(deduction) ? max : 0);
  const obt = clamp0(round2(max - ded));
  cells[2] = writeDeductionCell(cells[2], deduction);
  if (cells.length >= 4) cells[3] = writeMarkCell(cells[3], ded);
  if (cells.length >= 5) cells[4] = writeMarkCell(cells[4], obt);
}

function rowDeductionIsPlaceholder(cells, deductionIdx) {
  const ded = stripBold((cells && cells[deductionIdx]) || '');
  return !ded.trim() || isWholeQuestionUnattempted(ded);
}

/**
 * A table row inside the total must use a rubric question id. When the note
 * names a rubric question ("should be Q2.xiii" or "labeled as Q2.xiii"), that
 * working is written onto the rubric row. A label that names no rubric
 * question is moved below the Grand Total, where it stays visible and is not
 * added to the total. A rubric question that was parked under the total is
 * brought back above it. A Not Attempted stand-in for that same question is
 * dropped, so the graded row is the one that scores.
 */
export function filterPhantomQuestionRows(md, allowedIds) {
  if (!md || !allowedIds || !(allowedIds instanceof Set) || allowedIds.size === 0) {
    return { md, changed: false };
  }

  const deduped = keepLastTableBlock(String(md || ''));
  const lines = deduped.split('\n');

  let expectedCols = 0;
  let sampleLabel = '';
  let pastGrand = false;
  const bodyRows = []; // { lineIdx, cells, normId, deductionIdx, pastGrand }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim().startsWith('|')) continue;
    if (SEPARATOR_RE.test(line.trim())) continue;
    const raw = splitRawMarkdownCells(line);
    if (raw.length < 3) continue;
    const idStripped = stripBold(raw[0] || '');
    if (/^question$/i.test(idStripped)) {
      expectedCols = raw.length;
      pastGrand = false;
      continue;
    }
    const cells = splitMarkdownRowCells(line, expectedCols || raw.length);
    if (cells.length < 3) continue;
    if (GRAND_RE.test(idStripped)) {
      pastGrand = true;
      continue;
    }
    if (Q_TOTAL_RE.test(idStripped)) continue;
    const normId = normalizeQuestionId(idStripped);
    if (normId && isAllowedQuestionId(normId, allowedIds) && !sampleLabel) {
      sampleLabel = idStripped.replace(EXTRA_TAG_RE, '').trim();
    }
    bodyRows.push({ lineIdx: i, cells, normId, deductionIdx: 2, pastGrand });
  }

  const byNorm = new Map();
  for (const row of bodyRows) {
    if (!row.normId) continue;
    if (!byNorm.has(row.normId)) byNorm.set(row.normId, []);
    byNorm.get(row.normId).push(row);
  }

  const dropLineIdx = new Set();
  const rewritten = new Set();
  const parked = [];
  const lifted = [];
  let changed = deduped !== String(md || '');

  for (const row of bodyRows) {
    const allowed = row.normId && isAllowedQuestionId(row.normId, allowedIds);
    if (allowed && row.pastGrand) {
      const above = (byNorm.get(row.normId) || []).filter((t) => !t.pastGrand && !dropLineIdx.has(t.lineIdx));
      const gradedAbove = above.some((t) => !rowDeductionIsPlaceholder(t.cells, t.deductionIdx));
      if (gradedAbove) continue;
      for (const placeholder of above) dropLineIdx.add(placeholder.lineIdx);
      dropLineIdx.add(row.lineIdx);
      lifted.push(lines[row.lineIdx]);
      changed = true;
      continue;
    }
    if (allowed) continue;

    const deduction = stripBold(row.cells[row.deductionIdx] || '');
    const targetNorm = rubricTargetFromNote(deduction, allowedIds);
    const targets = targetNorm
      ? (byNorm.get(targetNorm) || []).filter((t) => !dropLineIdx.has(t.lineIdx))
      : [];

    if (targetNorm && targets.length === 1) {
      const target = targets[0];
      const targetDed = stripBold(target.cells[target.deductionIdx] || '');
      if (isWholeQuestionUnattempted(targetDed) || !targetDed.trim()) {
        const written = displayQuestionLabel(row.cells[0]) || row.normId || 'Q?';
        const moved = ensureQuestionNumberPenalty(deduction, written, cellNumber(target.cells[1]));
        writeRowDeduction(target.cells, moved);
        rewritten.add(target.lineIdx);
      }
      dropLineIdx.add(row.lineIdx);
      changed = true;
      continue;
    }
    if (targetNorm && targets.length === 0) {
      const label = insertedQuestionLabel(targetNorm, sampleLabel);
      row.cells[0] = writeDeductionCell(row.cells[0], label);
      row.normId = targetNorm;
      if (!byNorm.has(targetNorm)) byNorm.set(targetNorm, []);
      byNorm.get(targetNorm).push(row);
      rewritten.add(row.lineIdx);
      changed = true;
      continue;
    }

    dropLineIdx.add(row.lineIdx);
    parked.push(lines[row.lineIdx]);
    changed = true;
  }

  if (!changed && dropLineIdx.size === 0 && rewritten.size === 0 && parked.length === 0 && lifted.length === 0) {
    return { md: deduped, changed: false };
  }

  const out = [];
  let liftedInserted = false;
  const insertLifted = () => {
    if (liftedInserted || !lifted.length) return;
    out.push(...lifted);
    liftedInserted = true;
  };
  for (let i = 0; i < lines.length; i++) {
    if (dropLineIdx.has(i)) continue;
    const raw = splitRawMarkdownCells(lines[i]);
    if (GRAND_RE.test(stripBold(raw[0] || ''))) insertLifted();
    if (rewritten.has(i)) {
      const row = bodyRows.find((r) => r.lineIdx === i);
      out.push(row ? joinMarkdownRow(row.cells) : lines[i]);
    } else {
      out.push(lines[i]);
    }
  }
  insertLifted();
  if (parked.length) {
    let grandAt = -1;
    for (let i = 0; i < out.length; i++) {
      const raw = splitRawMarkdownCells(out[i]);
      if (GRAND_RE.test(stripBold(raw[0] || ''))) grandAt = i;
    }
    if (grandAt >= 0) out.splice(grandAt + 1, 0, ...parked);
    else out.push(...parked);
  }

  const next = out.join('\n');
  return { md: next, changed: next !== String(md || '') };
}

const formatDeduction = (d) => {
  const v = (d || '').trim();
  if (!v) return ' - Correct';
  if (/^none$/i.test(v) || /^full\s*marks$/i.test(v)) return ' - Correct';
  return ` - ${v}`;
};

function extractFooter(existing) {
  if (!existing) return '';
  // Prefer the "[Image_x.jpg] [N sheets]" trailer (and the course label
  // that Check appends after it). Don't pull the old "Total marks" phrase
  // itself — a stub one-line like "🟣 Total marks → 5 [Image] [1 sheets]"
  // would otherwise duplicate "Total marks" onto the rebuilt comment.
  const img = String(existing).match(
    /(\[[^\]]+\.(?:jpe?g|png|webp|gif)[^\]]*\]\s*\[\s*\d+\s+sheets?\s*\][\s\S]*)$/i
  );
  if (img) return img[1].trim();
  const idx = existing.search(/[Tt]otal\s+marks?[:\s]/);
  if (idx < 0) return '';
  const tail = existing.slice(idx);
  const after = tail.replace(
    /^Total\s+marks?\s*[:→\-–—>]*\s*[\d.]+(?:\s*\/\s*[\d.]+)?/i,
    ''
  );
  return after.trim();
}

export function tableToOneLineComments({ tableMd, studentName, existingComments = '', allowedIds = null, rubricMarks = null, paperTotal = null }) {
  const filtered = sanitizeBlockA(tableMd, allowedIds, rubricMarks, paperTotal).md;
  const t = computeTrueTotals(filtered, paperTotal);
  if (!t) return null;
  const { subQuestions, groupOrder, groupsMap, questionTotals, grandTotal } = t;
  const totalsMap = new Map(questionTotals.map(qt => [qt.id, qt]));

  const isFlat = groupOrder.every(p => {
    const list = groupsMap.get(p);
    return list.length === 1 && list[0].id === p;
  });

  // Use displayId in the one-line so any auto-corrected (extra) tag shows
  // up to the teacher (otherwise the comment would advertise totals that
  // don't match the visible labels).
  const labelOf = (sq) => sq.displayId || sq.id;
  const detailLine = (sq) =>
    `🔸 ${labelOf(sq)} → ${fmtMark(sq.obtained)}/${fmtMark(sq.max)}${formatDeduction(sq.deduction)}`;

  const groupChunks = [];
  if (isFlat) {
    const head = subQuestions
      .map(sq => `${labelOf(sq)} → ${fmtMark(sq.obtained)}/${fmtMark(sq.max)}`)
      .join(', ');
    const details = subQuestions.map(detailLine).join('| ');
    groupChunks.push(`${EMOJI_NUMS[0]} (${head}) ${details}`);
  } else {
    groupOrder.forEach((parent, idx) => {
      const list = groupsMap.get(parent);
      const emoji = EMOJI_NUMS[idx] || `(${idx + 1})`;
      const head = list
        .map(sq => `${labelOf(sq)} → ${fmtMark(sq.obtained)}/${fmtMark(sq.max)}`)
        .join(', ');
      const details = list.map(detailLine).join('| ');
      groupChunks.push(`${emoji} (${head}) ${details}`);
    });
  }

  const qTotalsStr = groupOrder
    .map(p => {
      const qt = totalsMap.get(p);
      return `${p} → ${fmtMark(qt.obtained)}/${fmtMark(qt.max)}`;
    })
    .join('| ');
  const grandStr = `🟣 Total marks: ${fmtMark(grandTotal.obtained)}/${fmtMark(grandTotal.max)}`;

  const headerPrefix = studentName ? `${studentName}:` : '';
  const footer = extractFooter(existingComments);
  const body =
    `${headerPrefix}| ${groupChunks.join('| ')}| 🔴(${qTotalsStr}) ${grandStr}` +
    (footer ? ` ${footer}` : '');

  return body;
}

// Rewrite the markdown table so every bold total row (and 4-column
// sub-row "Deduction Marks" cell) reflects the recomputed values.
// Sub-row Marks (max) and Deduction text are never touched. For legacy
// 3-column tables, sub-row Marks cells are rewritten when the AI
// self-corrected inside the Deduction cell.
//
// opts.allowedIds — optional Set from extractRubricQuestionIds(). When set,
// phantom rows (ids not in the rubric) are merged/dropped first.
export function correctTable(md, opts = {}) {
  const sanitized = sanitizeBlockA(md, opts.allowedIds, opts.rubricMarks, opts.paperTotal);
  let working = sanitized.md;
  let changed = sanitized.changed;

  const t = computeTrueTotals(working, opts.paperTotal);
  if (!t) return { md: working, changed };
  const { format, subQuestions, questionTotals, grandTotal } = t;
  // Look sub-question rows up by the EXACT label that appears in the markdown
  // (rawId) — the AI may have written "Q7 (extra)" or just "Q7", and the
  // auto-correction in computeTrueTotals can flip that. Falling back to
  // sq.id keeps legacy tables working when no (extra) tag is in play.
  const subMap = new Map(subQuestions.map(sq => [sq.rawId || sq.id, sq]));
  const totalsMap = new Map(questionTotals.map(qt => [qt.id, qt]));

  // Drop any duplicate table blocks first so we don't emit them back in the
  // corrected markdown. Tracks whether we actually changed anything so the
  // caller's "changed" signal still fires.
  const deduped = keepLastTableBlock(working);
  if (deduped !== working) changed = true;
  const lines = deduped.split('\n');

  const rewriteCell = (cell, newValue) => {
    const wasBold = /\*\*/.test(cell);
    if (stripBold(cell) === String(newValue)) return null;
    return wasBold ? `**${newValue}**` : String(newValue);
  };

  const expectedCols = format === 'new5' ? 5 : format === 'new4' ? 4 : 3;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim().startsWith('|')) continue;
    if (SEPARATOR_RE.test(line.trim())) continue;
    const raw = splitRawMarkdownCells(line);
    if (raw.length < 3) continue;
    const idStripped = stripBold(raw[0] || '');
    if (/^question$/i.test(idStripped)) continue;
    const cells = coalesceOverflowCells(raw, expectedCols);
    while (cells.length < expectedCols) cells.push('');
    const overflowed = raw.length > expectedCols;
    const isGrand = GRAND_RE.test(idStripped);
    const qm = idStripped.match(Q_TOTAL_RE);

    if (format === 'new4' || format === 'new5') {
      // Data cells: [0]=Question, [1]=Marks, [2]=Deduction, [3]=Deduction Marks, [4]=Obtained
      let row;
      let isSubRow = false;
      if (isGrand) row = grandTotal;
      else if (qm && totalsMap.has(qm[1])) row = totalsMap.get(qm[1]);
      else if (subMap.has(idStripped)) { row = subMap.get(idStripped); isSubRow = true; }
      if (!row) continue;

      let rowChanged = overflowed;
      if (isSubRow && row.displayId && row.displayId !== idStripped) {
        const newIdCell = rewriteCell(cells[0], row.displayId);
        if (newIdCell) { cells[0] = newIdCell; rowChanged = true; }
      }
      const newMaxCell = rewriteCell(cells[1], fmtMark(row.max));
      const newDedCell = rewriteCell(cells[3] || '', fmtMark(row.deductionMarks));
      if (newMaxCell) { cells[1] = newMaxCell; rowChanged = true; }
      if (newDedCell) { cells[3] = newDedCell; rowChanged = true; }
      if (format === 'new5') {
        const newObtCell = rewriteCell(cells[4] || '', fmtMark(row.obtained));
        if (newObtCell) { cells[4] = newObtCell; rowChanged = true; }
      }
      if (rowChanged) {
        lines[i] = joinMarkdownRow(cells);
        changed = true;
      }
      continue;
    }

    // Legacy 3-col: [Question, Marks (obtained/max), Deduction]
    let correct = null;
    let subForId = null;
    if (isGrand) correct = { obtained: grandTotal.obtained, max: grandTotal.max };
    else if (qm && totalsMap.has(qm[1])) {
      const qt = totalsMap.get(qm[1]);
      correct = { obtained: qt.obtained, max: qt.max };
    } else if (subMap.has(idStripped)) {
      const sq = subMap.get(idStripped);
      correct = { obtained: sq.obtained, max: sq.max };
      subForId = sq;
    }
    if (!correct) continue;
    let rowChanged = overflowed;
    if (subForId && subForId.displayId && subForId.displayId !== idStripped) {
      const newIdCell = rewriteCell(cells[0], subForId.displayId);
      if (newIdCell) { cells[0] = newIdCell; rowChanged = true; }
    }

    const rawMarksCell = cells[1] || '';
    const wasBold = /\*\*/.test(rawMarksCell);
    const newMarks = `${fmtMark(correct.obtained)}/${fmtMark(correct.max)}`;
    const wrappedMarks = wasBold ? `**${newMarks}**` : newMarks;
    if (stripBold(rawMarksCell) !== newMarks) {
      cells[1] = wrappedMarks;
      rowChanged = true;
    }
    if (rowChanged) {
      lines[i] = joinMarkdownRow(cells);
      changed = true;
    }
  }

  return { md: lines.join('\n'), changed };
}
